import { useQuery } from "@apollo/client";
import { Box, Link as MuiLink, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import { DateTime } from "luxon";
import { ReactNode } from "react";

import type { FlagRow } from "./FlagsTab";

import { gql } from "../../../generated-graphql";

/**
 * Drawer-only, lazy lookup of the live Linear project status for a flag's
 * linked project. Deliberately separate from `StaffFlagsList` (FlagsTab.tsx)
 * — the grid must never trigger a Linear API call for every row, so this
 * query is only mounted (and only fires, via `skip`) while a single flag's
 * drawer is open and that flag actually has a `linearProjectUrl`.
 */
const STAFF_FLAG_DELIVERY_QUERY = gql(`
  query StaffFlagDelivery($key: String!) {
    featureFlag(key: $key) {
      key
      linearProject {
        id
        name
        url
        state
        stateType
        targetDate
      }
    }
  }
`);

type LiveLinearProject = {
  name: string;
  url: string;
  state: string;
  stateType: string;
  targetDate?: string | null;
};

/**
 * Lightweight client-side hint for the "Linear URL" field. Accepts a Linear
 * project URL or an issue URL — flags whose work was delivered by a single
 * issue (engineering support, flags predating a project) link the
 * introducing issue instead of a project. Real validation (and the
 * authoritative error message, "Invalid Linear project or issue URL…")
 * happens server-side in `updateFeatureFlagMetadata` — this just catches an
 * obviously-wrong paste before it round-trips to the server.
 */
export function isLikelyLinearProjectUrl(url: string): boolean {
  const trimmed = url.trim();
  if (trimmed.length === 0) return true;
  return (
    trimmed.includes("linear.app/") &&
    (trimmed.includes("/project/") || trimmed.includes("/issue/"))
  );
}

export const LINEAR_PROJECT_URL_HINT =
  "Enter a Linear project or issue URL, e.g. https://linear.app/encamp/project/…";

/** stateType → semantic chip color. A switch (not nested ternaries) keeps every Linear ProjectStatus type explicit. */
function stateTypeToChipColor(stateType: string): StatusChipColor {
  switch (stateType) {
    case "started":
      return "info";
    case "completed":
      return "success";
    case "canceled":
      return "error";
    case "paused":
      return "warning";
    case "backlog":
    case "planned":
    // Issue workflow-state types (issue-linked flags share this chip).
    case "triage":
    case "unstarted":
    default:
      return "default";
  }
}

/**
 * Formats a date-only value (anticipatedReleaseDate, targetDate) without a
 * time component. Parsed in UTC because these are calendar dates stored at
 * midnight UTC — local-zone parsing would show the previous day anywhere
 * west of Greenwich.
 */
function formatFlagDate(dateLike: string): string {
  return DateTime.fromISO(dateLike, { zone: "utc" }).toLocaleString(DateTime.DATE_SHORT);
}

/** Matches the "Last edited {date}" line's timestamp formatting in the Annotation section. */
function formatFlagDateTime(dateLike: string): string {
  return new Date(dateLike).toLocaleString();
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  );
}

/**
 * Live status readout for the flag's linked Linear project: the project
 * name (or the raw URL, before the live lookup resolves or if it never
 * does) as a link, plus a state chip once we have one. Shared between read
 * mode and edit mode so staff editing the URL still see the currently
 * linked project's status without leaving the drawer. Takes already-fetched
 * data rather than querying itself, so the parent's single `useQuery` call
 * is the only network round-trip for the whole section.
 */
function LinearProjectStatus({
  url,
  loading,
  project,
}: {
  url: string;
  loading: boolean;
  project: LiveLinearProject | null;
}) {
  if (loading && !project) {
    return <Skeleton variant="text" width={180} />;
  }

  if (!project) {
    return (
      <Stack spacing={0.25}>
        <MuiLink href={url} target="_blank" rel="noopener noreferrer" underline="hover">
          <Typography variant="body2" noWrap>
            {url}
          </Typography>
        </MuiLink>
        <Typography variant="caption" color="text.secondary">
          Status unavailable
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <MuiLink href={project.url} target="_blank" rel="noopener noreferrer" underline="hover">
        <Typography variant="body2" noWrap>
          {project.name}
        </Typography>
      </MuiLink>
      <StatusChip color={stateTypeToChipColor(project.stateType)} label={project.state} />
    </Stack>
  );
}

/**
 * Read-mode-only "Anticipated release" row. Falls back to the live Linear
 * project's `targetDate` when no manual `anticipatedReleaseDate` is set —
 * the release date is "defaulted" from Linear until staff override it.
 * Suppressed entirely when neither value is available.
 */
function AnticipatedReleaseRow({
  anticipatedReleaseDate,
  targetDate,
}: {
  anticipatedReleaseDate: string | null;
  targetDate: string | null | undefined;
}) {
  if (anticipatedReleaseDate) {
    return (
      <DetailRow label="Anticipated release">
        <Typography variant="body2">{formatFlagDate(anticipatedReleaseDate)}</Typography>
      </DetailRow>
    );
  }

  if (!targetDate) return null;

  return (
    <DetailRow label="Anticipated release">
      <Stack spacing={0.25}>
        <Typography variant="body2">{formatFlagDate(targetDate)}</Typography>
        <Typography variant="caption" color="text.secondary">
          from Linear target date
        </Typography>
      </Stack>
    </DetailRow>
  );
}

type DeliveryEditValues = {
  linearProjectUrl: string;
  anticipatedReleaseDate: string | null;
};

/**
 * Helper text for the edit-mode release-date picker. Read mode falls back to
 * the Linear target date visually (see `AnticipatedReleaseRow`); edit mode
 * surfaces the same date here so staff overriding it can still see it.
 */
function releaseDateHelperText(
  formDate: string | null,
  targetDate: string | null | undefined
): string {
  if (!targetDate) {
    return "Defaults from the Linear project's target date";
  }
  if (formDate) {
    return `Overrides the Linear target date (${formatFlagDate(targetDate)})`;
  }
  return `Empty — defaults from the Linear target date (${formatFlagDate(targetDate)})`;
}

/** Shared shape passed to both the read-mode and edit-mode bodies below. */
type DeliveryBodyProps = {
  flag: FlagRow;
  loading: boolean;
  project: LiveLinearProject | null;
};

/**
 * Read-only "Delivery" body: label/value rows for the linked Linear
 * project, anticipated release, and creation info. Callers suppress the
 * whole section (heading included) when this has nothing to show.
 */
function ReadOnlyDeliveryBody({ flag, loading, project }: DeliveryBodyProps) {
  return (
    <Stack spacing={1.5}>
      {flag.linearProjectUrl && (
        <DetailRow label="Linear">
          <LinearProjectStatus url={flag.linearProjectUrl} loading={loading} project={project} />
        </DetailRow>
      )}
      <AnticipatedReleaseRow
        anticipatedReleaseDate={flag.anticipatedReleaseDate}
        targetDate={project?.targetDate}
      />
      {flag.annotationCreatedAt && (
        <DetailRow label="Created">
          <Typography variant="body2" color="text.secondary">
            {formatFlagDateTime(flag.annotationCreatedAt)}
            {flag.annotationCreatedBy ? ` by ${flag.annotationCreatedBy}` : ""}
          </Typography>
        </DetailRow>
      )}
    </Stack>
  );
}

/**
 * Editable "Delivery" body: the URL/date inputs, plus the same live status
 * and "Created" readouts shown in read mode (mirroring how the Annotation
 * section keeps its "Last edited" line visible while editing).
 */
function EditableDeliveryBody({
  flag,
  loading,
  project,
  form,
  onChange,
}: DeliveryBodyProps & {
  form: DeliveryEditValues;
  onChange: (patch: Partial<DeliveryEditValues>) => void;
}) {
  const urlHintFailed = !isLikelyLinearProjectUrl(form.linearProjectUrl);
  // Zone: UTC for the same previous-day reason as formatFlagDate — the
  // picker edits a calendar date, not a moment in time.
  const releaseDateValue = form.anticipatedReleaseDate
    ? DateTime.fromISO(form.anticipatedReleaseDate, { zone: "utc" })
    : null;

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Linear project or issue URL"
        fullWidth
        size="small"
        value={form.linearProjectUrl}
        onChange={(e) => onChange({ linearProjectUrl: e.target.value })}
        placeholder="https://linear.app/encamp/project/…"
        error={urlHintFailed}
        helperText={urlHintFailed ? LINEAR_PROJECT_URL_HINT : undefined}
      />
      {flag.linearProjectUrl && (
        <LinearProjectStatus url={flag.linearProjectUrl} loading={loading} project={project} />
      )}
      <DatePicker
        label="Anticipated release"
        value={releaseDateValue}
        onChange={(next) =>
          onChange({
            anticipatedReleaseDate: next?.isValid ? next.toISODate() : null,
          })
        }
        slotProps={{
          textField: {
            fullWidth: true,
            size: "small",
            helperText: releaseDateHelperText(form.anticipatedReleaseDate, project?.targetDate),
          },
          field: { clearable: true },
        }}
      />
      {flag.annotationCreatedAt && (
        <Typography variant="caption" color="text.secondary">
          Created {formatFlagDateTime(flag.annotationCreatedAt)}
          {flag.annotationCreatedBy ? ` by ${flag.annotationCreatedBy}` : ""}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * "Delivery" section of the flag detail drawer: the flag's linked Linear
 * project (with live status) and its anticipated release date. Split out of
 * FlagDetailDrawer.tsx to keep that file's size (and complexity) from
 * growing further — see the file header there. Owns the single live-status
 * query and delegates rendering to a read-mode or edit-mode body so neither
 * grows into its own hotspot.
 */
export function DeliverySection({
  flag,
  canManageFlag,
  form,
  onChange,
}: {
  flag: FlagRow;
  canManageFlag: boolean;
  form: DeliveryEditValues;
  onChange: (patch: Partial<DeliveryEditValues>) => void;
}) {
  const { data, loading } = useQuery(STAFF_FLAG_DELIVERY_QUERY, {
    variables: { key: flag.key },
    skip: !flag.linearProjectUrl,
    fetchPolicy: "cache-and-network",
  });
  const project = data?.featureFlag?.linearProject ?? null;

  if (!canManageFlag) {
    const hasNothingToShow =
      !flag.linearProjectUrl && !flag.anticipatedReleaseDate && !flag.annotationCreatedAt;
    if (hasNothingToShow) return null;
  }

  return (
    <Stack spacing={1}>
      <Typography variant="overline" color="text.secondary">
        Delivery
      </Typography>
      {canManageFlag ? (
        <EditableDeliveryBody
          flag={flag}
          loading={loading}
          project={project}
          form={form}
          onChange={onChange}
        />
      ) : (
        <ReadOnlyDeliveryBody flag={flag} loading={loading} project={project} />
      )}
    </Stack>
  );
}
